import React from "react";
import { useTranslation } from "react-i18next";

import style from "./entitlement-manual-activation.scss";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import Modal from "components/modal/Modal";
import { DownloadIcon } from "components/products/home-icons/DownloadIcon";
import { WarningBadge } from "components/warning/WarningBadge";
import { EntitlementState } from "domain/licenses";
import { licenseService } from "services/licenses/LicenseService";
import buttons from "styles/buttons.scss";
import form from "styles/form.scss";
import { isStringNotBlank } from "utils/commonFunctions";
import { useTheme } from "utils/useTheme";

import testIds from "testIds.json";

interface EntitlementManualActivationProps {
    visibility: boolean;
    onMarkAsActivated: () => void;
    setVisibility: (visibility: boolean) => void;
    entitlementState: string;
    entitlementId: string;
}

const EntitlementManualActivation: React.FC<EntitlementManualActivationProps> = ({
    visibility,
    setVisibility,
    onMarkAsActivated,
    entitlementId,
    entitlementState,
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [v2c, setV2c] = React.useState<string>();
    const [loading, setLoading] = React.useState<boolean>(true);
    const fullSetOfSteps = [
        t("Mcs.table.entitlementManualActivationDialog.fullSetOfStep1"),
        t("Mcs.table.entitlementManualActivationDialog.fullSetOfStep2"),
        t("Mcs.table.entitlementManualActivationDialog.fullSetOfStep3"),
        t("Mcs.table.entitlementManualActivationDialog.fullSetOfStep4"),
        t("Mcs.table.entitlementManualActivationDialog.fullSetOfStep5"),
        t("Mcs.table.entitlementManualActivationDialog.fullSetOfStep6"),
    ];

    const stepsForFailedWithoutV2c = [
        t("Mcs.table.entitlementManualActivationDialog.fullSetOfStep2"),
        t("Mcs.table.entitlementManualActivationDialog.fullSetOfStep3"),
        t("Mcs.table.entitlementManualActivationDialog.fullSetOfStep4"),
        t("Mcs.table.entitlementManualActivationDialog.fullSetOfStep5"),
        t("Mcs.table.entitlementManualActivationDialog.fullSetOfStep6"),
    ];

    const stepsWithV2c = [
        t("Mcs.table.entitlementManualActivationDialog.downloadV2cStep"),
        t("Mcs.table.entitlementManualActivationDialog.fullSetOfStep4"),
        t("Mcs.table.entitlementManualActivationDialog.fullSetOfStep5"),
        t("Mcs.table.entitlementManualActivationDialog.fullSetOfStep6"),
    ];

    function activateEntitlementManually() {
        const abortController = new AbortController();
        licenseService
            .editEntitlement(entitlementId, null, abortController, EntitlementState.MANUALLY_ACTIVATED)
            .then(() => {
                setVisibility(false);
                onMarkAsActivated();
                abortController.abort();
            })
            .finally(() => {
                setLoading(false);
            });
    }

    function deduceStepsBasedOnState() {
        if (isStringNotBlank(v2c)) {
            return stepsWithV2c;
        }
        if (
            EntitlementState.WAITING_FOR_C2V === entitlementState ||
            EntitlementState.IN_PROGRESS === entitlementState
        ) {
            return fullSetOfSteps;
        }
        return stepsForFailedWithoutV2c;
    }

    React.useEffect(() => {
        const abortController = new AbortController();
        licenseService
            .fetchV2cForEntitlement(entitlementId, abortController)
            .then((v2cContent) => {
                const cleanedContent = v2cContent.replace(/^"(.+)"$/, "$1");
                const unescapedContent = cleanedContent.replace(/\\"/g, '"').replace(/\\n/g, "\n");
                setV2c(unescapedContent);
            })
            .finally(() => {
                setLoading(false);
            });
        return () => {
            abortController.abort();
        };
    }, []);

    return (
        <Modal
            isOpen={visibility}
            hideModal={() => setVisibility(false)}
            modalTitle={t("Mcs.table.entitlementManualActivationDialog.title")}
            width={"800px"}
        >
            {loading ? (
                <LoadingIndicator />
            ) : (
                <>
                    <div>
                        <div>
                            <WarningBadge
                                note={
                                    EntitlementState.FAILED === entitlementState
                                        ? t("Mcs.table.entitlementManualActivationDialog.warningFailedText")
                                        : t("Mcs.table.entitlementManualActivationDialog.warningPendingText")
                                }
                            />
                        </div>
                        <div>
                            <ol className={style.stepsList}>
                                {deduceStepsBasedOnState().map((step, index) => (
                                    <li key={index} className={style.stepItem}>
                                        <span className={style.stepNumber}></span>
                                        <span className={style.stepText}>
                                            {step}
                                            {step ===
                                                t("Mcs.table.entitlementManualActivationDialog.downloadV2cStep") &&
                                                v2c && (
                                                    <div className={style.downloadButtonContainer}>
                                                        <button
                                                            className={style.downloadButton}
                                                            onClick={() => {
                                                                const blob = new Blob([v2c], {
                                                                    type: "application/xml",
                                                                });
                                                                const url = URL.createObjectURL(blob);
                                                                const link = document.createElement("a");
                                                                link.href = url;
                                                                link.download = `${entitlementId}.v2c`;
                                                                document.body.appendChild(link);
                                                                link.click();
                                                                document.body.removeChild(link);
                                                                URL.revokeObjectURL(url);
                                                            }}
                                                        >
                                                            V2C
                                                            <DownloadIcon color={theme.iconFillColor} />
                                                        </button>
                                                    </div>
                                                )}
                                        </span>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    </div>

                    <div className={form.buttonContainer}>
                        <button
                            className={buttons.primaryButtonWithoutIcon}
                            onClick={() => activateEntitlementManually()}
                            data-testid={testIds.workArea.mcs.entitlementManualActivationDialog.markAsActivatedButton}
                        >
                            {t("Mcs.table.entitlementManualActivationDialog.markAsActivated")}
                        </button>
                    </div>
                </>
            )}
        </Modal>
    );
};

export default EntitlementManualActivation;
